import { registry } from '@geo-admin/shared/contract/api/index.ts';
import type { Query } from '@geo-admin/shared/contract/api/registry.ts';
import {
  ONBOARDING_DAYS_DEFAULT,
  ONBOARDING_DAYS_MAX,
  ONBOARDING_DAYS_MIN,
} from '@geo-admin/shared/contract/probe-values.ts';
import { Link } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import { useState } from 'react';
import { DataTable } from '../components/data-table.tsx';
import { Badge, Card, CardBody } from '../components/ui/card.tsx';
import { Button } from '../components/ui/button.tsx';
import { Dialog } from '../components/ui/dialog.tsx';
import { FieldError, Input, Label, Select } from '../components/ui/form.tsx';
import { ErrorBox, Loading } from '../components/ui/states.tsx';
import { useApi, useApiMutation } from '../lib/api.ts';
import { PageTitle } from '../components/PageTitle.tsx';
import { BrandKindTabs, useBrandKind } from '../components/BrandKindTabs.tsx';

export function QueriesPage() {
  const [brandId, setBrandId] = useState('');
  const [includeDeleted, setIncludeDeleted] = useState(false);
  // 「실제 | 테스트」 탭 — 목록·고객 선택지·질문 추가 창의 고객이 전부 탭을 따른다(261023-test-brand)
  const kind = useBrandKind();
  const brands = useApi(registry.listBrands, { query: { brandKind: kind } });
  // 탭을 바꾸면 다른 쪽 고객은 선택지에 없다 — 그때는 「전체」로 본다
  const picked = brands.data?.brands.some((c) => c.id === brandId) ? brandId : '';
  const queries = useApi(registry.listQueries, {
    query: { brandId: picked || undefined, includeDeleted: includeDeleted ? 'true' : undefined, brandKind: kind },
  });
  const [editing, setEditing] = useState<Query | 'new' | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  const del = useApiMutation(registry.deleteQuery);

  const columns: ColumnDef<Query, unknown>[] = [
    {
      header: '질문',
      accessorKey: 'query',
      cell: ({ row }) => (
        <Link to="/admin/queries/$id" params={{ id: row.original.id }} className="font-medium hover:underline">
          {row.original.query}
        </Link>
      ),
    },
    { header: '고객', accessorKey: 'brandName' },
    {
      header: '목표 브랜드명',
      id: 'brands',
      cell: ({ row }) => (
        <div className="flex flex-wrap gap-1">
          {row.original.brandNames.map((b) => (
            <Badge key={b}>{b}</Badge>
          ))}
        </div>
      ),
    },
    { header: '일일 횟수', accessorKey: 'samplesPerDay' },
    {
      header: '상태',
      id: 'state',
      cell: ({ row }) =>
        row.original.deleted ? (
          <Badge tone="red">삭제됨</Badge>
        ) : row.original.samplesPerDay === 0 ? (
          <Badge tone="amber">멈춤</Badge>
        ) : (
          <Badge tone="green">수집 중</Badge>
        ),
    },
    {
      header: '온보딩',
      id: 'onboarding',
      // 기간은 질문마다 다르다 — 상수로 적지 않는다 (261001-onboarding-period-per-query)
      cell: ({ row }) =>
        row.original.onboardingEnabled === false ? (
          <span className="text-xs text-slate-400">미적용</span>
        ) : row.original.onboardingDays === null ? null : (
          <span className="text-xs text-slate-500 tabular-nums">{row.original.onboardingDays}일</span>
        ),
    },
    {
      header: '',
      id: 'actions',
      cell: ({ row }) =>
        row.original.deleted ? null : (
          <div className="flex justify-end gap-1">
            <Button size="sm" variant="outline" onClick={() => setEditing(row.original)}>
              편집
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() =>
                confirm('수집을 멈추고 목록에서 뺍니다. 과거 샘플은 남지만 되살릴 수는 없습니다.') &&
                del.mutate({ params: { id: row.original.id } })
              }
            >
              삭제
            </Button>
          </div>
        ),
    },
  ];

  return (
    <>
      <PageTitle
        title="타깃 질문 관리"
        description="목표 브랜드명은 고객에 있습니다 — 고객 화면에서 고치면 그 고객의 질문 전부가 따라가고, 과거 샘플도 다시 판정됩니다"
        actions={<Button onClick={() => setEditing('new')}>질문 추가</Button>}
      />
      <BrandKindTabs />
      {warnings.length > 0 ? (
        <Card className="border-amber-300 bg-amber-50">
          <CardBody className="space-y-1 text-sm text-amber-900">
            {warnings.map((w) => (
              <p key={w}>⚠ {w}</p>
            ))}
            <Button size="sm" variant="ghost" onClick={() => setWarnings([])}>
              닫기
            </Button>
          </CardBody>
        </Card>
      ) : null}
      <Card>
        <CardBody className="flex flex-wrap items-end gap-3">
          <Label className="w-64">
            고객
            <Select value={picked} onChange={(e) => setBrandId(e.target.value)}>
              <option value="">전체</option>
              {brands.data?.brands.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
          </Label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={includeDeleted} onChange={(e) => setIncludeDeleted(e.target.checked)} />{' '}
            삭제된 질문 포함
          </label>
        </CardBody>
      </Card>
      <Card>
        {queries.isLoading ? (
          <Loading />
        ) : queries.error ? (
          <ErrorBox error={queries.error} />
        ) : (
          <DataTable
            columns={columns}
            data={queries.data?.queries ?? []}
            empty={kind === 'test' ? '테스트 브랜드의 질문이 없습니다' : '등록된 질문이 없습니다'}
          />
        )}
      </Card>
      <QueryDialog
        editing={editing}
        onClose={() => setEditing(null)}
        onWarnings={setWarnings}
        brands={brands.data?.brands ?? []}
      />
    </>
  );
}

function QueryDialog({
  editing,
  onClose,
  onWarnings,
  brands,
}: {
  editing: Query | 'new' | null;
  onClose: () => void;
  onWarnings: (w: string[]) => void;
  brands: { id: string; name: string; brandNames: string[] }[];
}) {
  const create = useApiMutation(registry.createQuery);
  const update = useApiMutation(registry.updateQuery);
  const current = editing === 'new' ? null : editing;
  const [error, setError] = useState<string | null>(null);
  const [brandId, setBrandId] = useState('');
  // 브랜드명은 고객의 것이다 — 질문에서 받지 않는다 (261008-client-is-brand)
  const brandNames = current ? current.brandNames : (brands.find((c) => c.id === brandId)?.brandNames ?? []);

  const submit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    const f = new FormData(e.currentTarget);
    const samplesPerDay = Number(f.get('samplesPerDay'));
    // 여기서 바꾼 값은 **다음 스냅샷부터** 효력이 있다 — 이미 만들어진 리포트는 움직이지 않는다
    const onboardingEnabled = f.get('onboardingEnabled') === 'on';
    const onboardingDays = Number(f.get('onboardingDays'));
    try {
      // 문구는 생성 때만 보낸다 — 수정 계약에 없다 (260919-query-text-immutable)
      const r = current
        ? await update.mutateAsync({
            params: { id: current.id },
            body: { samplesPerDay, onboardingEnabled, onboardingDays },
          })
        : await create.mutateAsync({
            body: {
              brandId: String(f.get('brandId')),
              query: String(f.get('query') ?? ''),
              samplesPerDay,
              onboardingEnabled,
              onboardingDays,
            },
          });
      onWarnings(r.warnings);
      onClose();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  return (
    <Dialog open={editing !== null} onClose={onClose} title={current ? '질문 편집' : '질문 추가'}>
      <form key={current?.id ?? 'new'} className="grid gap-3" onSubmit={submit}>
        {current ? (
          <p className="text-sm text-slate-500">
            고객: {current.brandName} (소속 고객은 바꿀 수 없습니다 — 옮기려면 새 질문을 만들고 이것을 삭제합니다)
          </p>
        ) : (
          <Label>
            고객
            <Select name="brandId" required value={brandId} onChange={(e) => setBrandId(e.target.value)}>
              <option value="" disabled>
                선택
              </option>
              {brands.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
          </Label>
        )}
        {current ? (
          <p className="text-sm text-slate-500">
            문구: <span className="font-medium text-slate-700">{current.query}</span>
            <br />
            (문구는 바꿀 수 없습니다 — 바꾸려면 새 질문을 만들고 이것을 삭제합니다. 과거 샘플과 추이는 그대로 남습니다)
          </p>
        ) : (
          <Label>
            ChatGPT 에 보낼 문구
            <Input name="query" required minLength={2} maxLength={300} />
          </Label>
        )}
        {current || brandId ? (
          <p className="text-sm text-slate-500">
            목표 브랜드명:{' '}
            {brandNames.length > 0 ? (
              <span className="font-medium text-slate-700">{brandNames.join(' · ')}</span>
            ) : (
              <span className="text-bad">없음 — 고객 화면에서 먼저 입력해 주세요</span>
            )}
            <br />
            (브랜드명은 고객 단위입니다 — 고객 편집에서 고칩니다)
          </p>
        ) : null}
        <Label>
          하루 샘플 수 (0 = 수집 멈춤)
          <Input
            name="samplesPerDay"
            type="number"
            min={0}
            max={48}
            defaultValue={current?.samplesPerDay ?? 10}
            required
          />
        </Label>
        <label className="flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            name="onboardingEnabled"
            defaultChecked={current ? (current.onboardingEnabled ?? true) : true}
            className="mt-1"
          />
          <span>
            온보딩 적용
            <span className="block text-xs text-slate-500">
              등록일부터 아래 기간 동안 리포트 수치를 완화해 보여줍니다. 바꾸면 <b>다음 날 리포트부터</b> 반영되고, 이미
              만들어진 리포트는 그대로입니다.
            </span>
          </span>
        </label>
        <Label>
          온보딩 기간 (일)
          <Input
            name="onboardingDays"
            type="number"
            min={ONBOARDING_DAYS_MIN}
            max={ONBOARDING_DAYS_MAX}
            defaultValue={current?.onboardingDays ?? ONBOARDING_DAYS_DEFAULT}
            required
          />
          <span className="block text-xs text-slate-500">
            계수가 0.5 에서 1.0 까지 오르는 데 걸리는 날수입니다. 길수록 초기 수치가 더 낮게 나옵니다.
          </span>
        </Label>
        <FieldError>{error}</FieldError>
        <Button type="submit">저장</Button>
      </form>
    </Dialog>
  );
}
